import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, MapPin, Bed, Bath, Plus, Loader2, Star } from 'lucide-react';
import HouseService from '../../services/HouseService';
import { Card, toast, LoadingState, ErrorState, EmptyState, PageHeader } from '../../components/v2/V2Components';
import { PLACEHOLDER_IMAGES } from '../../constants/images';
import usePageView from '../../hooks/usePageView';
import analytics from '../../services/AnalyticsService';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';

const HouseList = () => {
    usePageView(ANALYTICS_EVENTS.HOUSES_LIST_VIEWED);
    const navigate = useNavigate();
    const [houses, setHouses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // House id currently being promoted to primary; drives the per-card
    // spinner and disables the button so a double-click can't fire twice.
    const [settingPrimaryId, setSettingPrimaryId] = useState(null);
    // Synchronous in-flight latch. State updates don't apply until the next
    // render, so two clicks in the same tick could both pass the state guard
    // before settingPrimaryId commits. A ref updates immediately, closing that
    // window regardless of render timing.
    const settingPrimaryRef = useRef(false);
    // Mount guard for the in-flight getHouses() promise. If the user leaves
    // /houses before it settles, the resolved branch must not call navigate()
    // — a stale redirect would yank them off the new page they're on.
    const isActiveRef = useRef(true);

    useEffect(() => {
        isActiveRef.current = true;
        fetchHouses();
        return () => {
            isActiveRef.current = false;
        };
    }, []);

    const fetchHouses = async () => {
        // First-run shortcut: when the user's only house is still the
        // auto-seeded placeholder (api/clerk_auth _seed_placeholder_house),
        // skip the list and drop them straight into the edit form. The
        // backend flips is_placeholder=false on the first save with a real
        // address (api/houses.py update_house), so subsequent visits render
        // the list normally. Mirrors the create-form redirect in HouseForm.
        let redirectTo = null;
        try {
            const data = await HouseService.getHouses();
            if (!isActiveRef.current) return;
            if (Array.isArray(data) && data.length === 1 && data[0]?.is_placeholder && data[0]?.id) {
                redirectTo = `/houses/${data[0].id}/edit`;
            } else {
                setHouses(data);
                setError(null);
            }
        } catch (error) {
            if (!isActiveRef.current) return;
            // Handle specific error codes
            if (error.code === 401) {
                setError('Authentication required. Please log in again.');
            } else {
                setError(error.message || 'Failed to load houses. Please try again later.');
            }
        } finally {
            // A return/throw from a finally block can swallow exceptions
            // from the surrounding try/catch (no-unsafe-finally), so the
            // mount guard is expressed as an `if` rather than an early
            // return here.
            if (isActiveRef.current) {
                if (redirectTo) {
                    // Keep loading=true so the spinner stays up while the
                    // route swaps — flipping it off here would flash the
                    // empty state.
                    navigate(redirectTo, { replace: true });
                } else {
                    setLoading(false);
                }
            }
        }
    };

    // house_count distinguishes the two add-property entry points: a count
    // of 0 means the empty-state CTA, >0 means the header button. It also
    // gives the house-click event portfolio-size context.
    const handleAddClick = () => {
        analytics.track(ANALYTICS_EVENTS.HOUSES_LIST_ADD_CLICKED, {
            house_count: houses.length,
        });
        navigate('/houses/add');
    };

    const handleHouseClick = (houseId) => {
        analytics.track(ANALYTICS_EVENTS.HOUSES_LIST_HOUSE_CLICKED, {
            house_count: houses.length,
        });
        navigate(`/houses/${houseId}`);
    };

    // Promote a house to primary. stopPropagation keeps the card's
    // navigate-on-click from firing. The backend atomically clears the flag
    // on every other house, so we optimistically reflect that locally rather
    // than refetching the whole list.
    const handleSetPrimary = async (e, houseId) => {
        e.stopPropagation();
        // Ref guard runs synchronously — blocks a same-tick second click that
        // the state guard below would miss until the next render.
        if (settingPrimaryRef.current) return;
        settingPrimaryRef.current = true;
        setSettingPrimaryId(houseId);
        try {
            await HouseService.setPrimaryHouse(houseId);
            setHouses((prev) =>
                prev.map((h) => ({ ...h, is_primary: h.id === houseId }))
            );
            toast.success('Primary home updated');
        } catch (err) {
            toast.error(err.message || 'Failed to set primary home');
        } finally {
            settingPrimaryRef.current = false;
            setSettingPrimaryId(null);
        }
    };

    // The API `url` field on house.images is authoritative (presigned R2 URL
    // or null). Never fabricate a /static/uploads/houses/<filename> path —
    // those files don't exist on disk in production and would 404.
    const getHouseImage = (house) => {
        const gallery = (house.images || []).filter((img) => !img.is_floor_plan);
        const pick = gallery.find((img) => img.is_primary) || gallery[0];
        return pick?.url || PLACEHOLDER_IMAGES.house;
    };

    const formatCurrency = (value) => {
        if (value == null || value === '') return 'N/A';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    if (loading) {
        return <LoadingState />;
    }

    if (error) {
        return (
            <ErrorState
                title="Error Loading Properties"
                message={error}
                onRetry={() => { setLoading(true); setError(null); fetchHouses(); }}
            />
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            <PageHeader
                title="My Properties"
                subtitle="Manage your houses and their information"
                actions={
                    <button
                        type="button"
                        onClick={handleAddClick}
                        className="flex items-center space-x-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-all shadow-sm"
                    >
                        <Plus size={20} />
                        <span>Add Property</span>
                    </button>
                }
            />

            {/* Content */}
            {houses.length === 0 ? (
                <Card className="border-dashed border-2 bg-transparent shadow-none">
                    <EmptyState
                        icon={Home}
                        title="No Properties Yet"
                        message="Add your first property to get started with tracking inventory, maintenance, and documents for your home."
                        actionLabel="Add Your First Property"
                        onAction={handleAddClick}
                    />
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {houses.map((house) => (
                        <Card
                            key={house.id}
                            data-testid="house-card"
                            className="group h-full overflow-hidden hover:shadow-md cursor-pointer transition-all duration-300 transform hover:-translate-y-1"
                            onClick={() => handleHouseClick(house.id)}
                        >
                            {/* Image Section */}
                            <div className="relative h-48 overflow-hidden">
                                {house.is_primary && (
                                    <div className="absolute top-0 right-0 z-10">
                                        <div
                                            data-testid="primary-badge"
                                            className="bg-[var(--primary)] text-white text-xs font-bold px-3 py-1 rounded-bl-lg shadow-sm"
                                        >
                                            Primary
                                        </div>
                                    </div>
                                )}
                                <img
                                    src={getHouseImage(house)}
                                    alt={house.name}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    onError={(e) => {
                                        // Fall back to the placeholder if a presigned
                                        // URL has expired / 404s. Clear the handler
                                        // first so the placeholder can't loop.
                                        if (e.currentTarget.src !== PLACEHOLDER_IMAGES.house) {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = PLACEHOLDER_IMAGES.house;
                                        }
                                    }}
                                />
                                {/* Overlay Gradient */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            </div>

                            {/* Info Section */}
                            <div className="p-5">
                                <div className="flex justify-between items-start mb-2 gap-2">
                                    <h3 className="text-xl font-bold text-[var(--text-main)] group-hover:text-[var(--primary)] transition-colors">
                                        {house.name}
                                    </h3>
                                    {house.is_primary ? (
                                        <span className="flex items-center gap-1 text-xs font-semibold text-[var(--primary)] whitespace-nowrap mt-1">
                                            <Star size={14} className="fill-[var(--primary)]" />
                                            Primary
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={(e) => handleSetPrimary(e, house.id)}
                                            disabled={settingPrimaryId !== null}
                                            className="flex items-center gap-1 text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--primary)] border border-[var(--border)] rounded-lg px-2 py-1 whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {settingPrimaryId === house.id ? (
                                                <Loader2 size={14} className="animate-spin" />
                                            ) : (
                                                <Star size={14} />
                                            )}
                                            Set as primary
                                        </button>
                                    )}
                                </div>

                                {house.address && (
                                    <div className="flex items-start text-[var(--text-secondary)] text-sm mb-4 min-h-[40px]">
                                        <MapPin size={16} className="mr-2 mt-0.5 flex-shrink-0 opacity-70" />
                                        <span>
                                            {house.address.street}
                                            {house.address.city && `, ${house.address.city}`}
                                            {house.address.state && `, ${house.address.state}`}
                                        </span>
                                    </div>
                                )}

                                {/* Specs Grid */}
                                <div className="grid grid-cols-3 gap-2 py-3 border-t border-[var(--border)] border-b mb-3">
                                    <div className="flex flex-col items-center justify-center p-1">
                                        <div className="flex items-center text-[var(--text-muted)] text-xs uppercase tracking-wider mb-1">
                                            <Bed size={14} className="mr-1" /> Beds
                                        </div>
                                        <span className="font-semibold text-[var(--text-main)]">{house.bedrooms ?? '-'}</span>
                                    </div>
                                    <div className="flex flex-col items-center justify-center p-1 border-l border-r border-[var(--border)]">
                                        <div className="flex items-center text-[var(--text-muted)] text-xs uppercase tracking-wider mb-1">
                                            <Bath size={14} className="mr-1" /> Baths
                                        </div>
                                        <span className="font-semibold text-[var(--text-main)]">{house.bathrooms ?? '-'}</span>
                                    </div>
                                    <div className="flex flex-col items-center justify-center p-1">
                                        <div className="flex items-center text-[var(--text-muted)] text-xs uppercase tracking-wider mb-1">
                                            <Home size={14} className="mr-1" /> Sq Ft
                                        </div>
                                        <span className="font-semibold text-[var(--text-main)]">{house.square_footage ? house.square_footage.toLocaleString() : '-'}</span>
                                    </div>
                                </div>

                                {/* Footer Stats */}
                                <div className="flex justify-between items-center text-sm">
                                    <div className="flex flex-col">
                                        <span className="text-[var(--text-muted)] text-xs">Value</span>
                                        <span className="font-bold text-[var(--primary)]">
                                            {formatCurrency(house.current_estimated_value ?? house.purchase_price)}
                                        </span>
                                    </div>
                                    <div className="flex flex-col text-right">
                                        <span className="text-[var(--text-muted)] text-xs">Built</span>
                                        <span className="font-medium text-[var(--text-main)]">{house.year_built || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default HouseList;
